import { css } from "glamor";
import React from "react";
import { colors, spacing } from "../library";
import { uiColors } from "../library/theme";
import { formatRemainingTime } from "../../utils/studyPlanFormatters";
import StudyGoalModalContainer from "./StudyGoalModalContainer";

// ── container ────────────────────────────────────────────────────────────────

const containerClass = css({
  border: `1px solid ${colors.greyClear}`,
  borderRadius: 12,
  boxShadow: `0px 2px 4px ${uiColors.border}`,
  padding: `${spacing.m} ${spacing.l}`,
  "@media (max-width: 768px)": {
    padding: spacing.m,
  },
});

const rowClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.l,
  "@media (max-width: 900px)": {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: spacing.m,
  },
});

const infoGroupClass = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.l,
  flexWrap: "wrap",
  "@media (max-width: 900px)": {
    gap: spacing.m,
  },
});

const sectionClass = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
});

const sectionIconClass = css({
  width: 32,
  height: 32,
  borderRadius: "50%",
  backgroundColor: colors.greyLighter,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: colors.greyDark,
  fontSize: 14,
});

const sectionTextClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const sectionLabelStyle = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  color: colors.greyDark,
  lineHeight: 1.2,
};

const sectionValueStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: colors.greyShadow,
  lineHeight: 1.3,
};

const sectionValueBoldStyle = {
  fontWeight: 700,
  color: colors.greyShadow,
};

const completionIconClass = css({
  width: 32,
  height: 32,
  borderRadius: "50%",
  backgroundColor: "#E8F5E9",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  color: "#43A047",
  fontSize: 14,
});

const completionValueStyle = {
  fontSize: 14,
  fontWeight: 700,
  color: "#43A047",
  lineHeight: 1.3,
};

const noStudyGoalClass = css({
  textAlign: "center",
  padding: spacing.m,
});

const noStudyGoalTitleStyle = {
  fontSize: 16,
  fontWeight: 700,
  marginBottom: spacing.xs,
};

const noStudyGoalDescStyle = {
  margin: "0 auto",
  marginBottom: spacing.s,
  maxWidth: 500,
  color: colors.greyDark,
};

// ── helpers ──────────────────────────────────────────────────────────────────

function formatCompletionDate(date) {
  if (!date) return "--";
  return Intl.DateTimeFormat("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

// ── component ────────────────────────────────────────────────────────────────

export default function StudyGoal({ studyGoal, course, studyDaysCount = 0, completionDate }) {
  if (!studyGoal) {
    return (
      <div className={containerClass}>
        <div className={noStudyGoalClass}>
          <h4 style={noStudyGoalTitleStyle}>Plano de estudos</h4>
          <p style={noStudyGoalDescStyle}>
            Você ainda não tem uma meta de estudo definida. Que tal começar
            agora e dar o primeiro passo rumo ao seu objetivo?
          </p>
          <StudyGoalModalContainer />
        </div>
      </div>
    );
  }

  const courseProgress = course?.meta?.progress;
  const dailyBudget = studyGoal.dayStudyDurationSeconds;

  return (
    <div className={containerClass}>
      <div className={rowClass}>
        <div className={infoGroupClass}>
          {/* Meta diaria */}
          <div className={sectionClass}>
            <div className={sectionIconClass}>
              <i
                className="clock outline icon"
                style={{ margin: 0, lineHeight: 1 }}
              />
            </div>
            <div className={sectionTextClass}>
              <span style={sectionLabelStyle}>META DIÁRIA</span>
              <span style={sectionValueStyle}>
                <strong style={sectionValueBoldStyle}>
                  {formatRemainingTime(dailyBudget)}
                </strong>
                {" / dia"}
              </span>
            </div>
          </div>

          {/* Dias de estudo restantes */}
          {studyDaysCount > 0 && (
            <div className={sectionClass}>
              <div className={sectionIconClass}>
                <i
                  className="book icon"
                  style={{ margin: 0, lineHeight: 1 }}
                />
              </div>
              <div className={sectionTextClass}>
                <span style={sectionLabelStyle}>DIAS DE ESTUDO</span>
                <span style={sectionValueStyle}>
                  <strong style={sectionValueBoldStyle}>
                    {studyDaysCount}
                  </strong>
                </span>
              </div>
            </div>
          )}

          {/* Conclusao estimada */}
          <div className={sectionClass}>
            <div className={completionIconClass}>
              <i
                className="flag checkered icon"
                style={{ margin: 0, lineHeight: 1 }}
              />
            </div>
            <div className={sectionTextClass}>
              <span style={sectionLabelStyle}>CONCLUSÃO ESTIMADA</span>
              <span style={completionValueStyle}>
                {courseProgress >= 1
                  ? "Curso concluído!"
                  : formatCompletionDate(completionDate)}
              </span>
            </div>
          </div>
        </div>

        {/* Reconfigure link */}
        <StudyGoalModalContainer />
      </div>
    </div>
  );
}
